import { Box, Icon, IconButton, Stack, Typography } from '@mui/material';
import {
  AttachmentIcon,
  MailIcon,
  SimpleCard,
  ThreadIcon,
  TypographyWithOverflowHint,
} from '@schooly/style';
import { getRelativeTimeLabel } from '@schooly/utils/date';
import { getUserFullName } from '@schooly/utils/user-helpers';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Notification } from './utils';

type NotificationCardProps = {
  notification: Notification;
  onNotificationClick: () => void;
};

export const NotificationCard = ({ notification, onNotificationClick }: NotificationCardProps) => {
  const { formatMessage } = useIntl();

  const { date, type } = notification;
  const isThreadNotification = notification.type === 'thread';
  const isMessageUnread = notification.type === 'message' && !notification.message.recipient_read;

  const messageSubtitle =
    notification.type === 'message'
      ? `${getUserFullName(notification.message.sender)} ${formatMessage({
          id: 'messages-CCYouOnMessage',
        })}`
      : '';

  const threadSubtitle =
    notification.type === 'thread'
      ? formatMessage({
          id:
            notification.unreadThreads.length > 1
              ? 'messages-LeftCommentsOnThread'
              : 'messages-LeftCommentOnThread',
        })
      : '';

  const title =
    notification.type === 'thread' ? notification.rootEntity.title : notification.message.title;

  const hasAttachments =
    type === 'thread'
      ? notification.rootEntity.has_attachments
      : !!notification.message.attachments?.length;

  const timeLabel = useMemo(() => {
    if (!date) return '';
    return getRelativeTimeLabel(date, (d, v) => formatMessage(d, v));
  }, [date, formatMessage]);

  return (
    <SimpleCard
      onClick={onNotificationClick}
      actions={
        <Stack alignSelf="flex-end" alignItems="flex-end" mr={1} mt={1} gap={0.75}>
          <Box
            sx={{
              width: 8,
              height: 8,
              backgroundColor: 'success.main',
              borderRadius: '50%',
              mr: 0.5,
              visibility: isThreadNotification || isMessageUnread ? 'visible' : 'hidden',
            }}
          />
          <Typography color="text.secondary" whiteSpace="nowrap">
            {timeLabel}
          </Typography>
        </Stack>
      }
      avatar={
        <IconButton
          sx={{
            width: 40,
            height: 40,
            backgroundColor:
              notification.type === 'thread' ? 'success.superLight' : 'common.lightBg',
            border: (theme) =>
              notification.type === 'thread'
                ? `1px solid ${theme.palette.common.green6}`
                : `1px solid ${theme.palette.common.light2}`,
            svg: {
              width: 20,
              height: 20,
              color: notification.type === 'thread' ? 'success.main' : 'common.grey3',
            },
          }}
        >
          {notification.type === 'thread' ? <ThreadIcon /> : <MailIcon />}
        </IconButton>
      }
      sx={{
        borderBottom: 'none',
        position: 'relative',
        '&:after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: '88%',
          height: '1px',
          backgroundColor: 'divider',
        },
      }}
    >
      <Stack gap={0.25} pl={1.25} pb={0.75}>
        <Stack direction="row" alignItems="center" gap={0.5}>
          {hasAttachments && (
            <Icon>
              <AttachmentIcon />
            </Icon>
          )}
          <Box>
            <TypographyWithOverflowHint maxWidth={400} variant="h3" color="common.grey2">
              {title}
            </TypographyWithOverflowHint>
          </Box>
        </Stack>
        <Typography variant="body2" color="common.grey3">
          {messageSubtitle || threadSubtitle}
        </Typography>
      </Stack>
    </SimpleCard>
  );
};
